import { useState, useEffect, useMemo } from "react";

import {
  Close as CloseIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  IconButton,
} from "@mui/material";
import {
  postBulkEditSearch,
  type Selector,
  type SurveyMatch,
  type PostBulkEditSearchResponse,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate } from "react-router-dom";

/** Strip BOM and normalize smart quotes / em-dashes / NBSP that users often
 *  paste from rich-text sources — the resulting string is safe to feed into
 *  JSON.parse server-side. */
const normalizeJsonReplacement = (s: string): string =>
  s
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/\u00A0/g, " ")
    .replace(/^\uFEFF/, "");

type TargetType =
  | "string"
  | "element"
  | "page"
  | "add_property"
  | "remove_property"
  | "add_element"
  | "delete_element"
  | "set_property"
  | "update_property"
  | "rename_property"
  | "append_to_array"
  | "remove_from_array_by_match";
const SELECTOR_SUPPORTED_OPS: TargetType[] = [
  "element",
  "add_property",
  "remove_property",
  "delete_element",
  "set_property",
  "update_property",
  "rename_property",
  "append_to_array",
  "remove_from_array_by_match",
];

type TargetConfig = "main" | "condensed" | "both";
type ViewStep = "search" | "results";

interface BulkEditModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onSuccess?: () => void;
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  open,
  onClose,
  orgId,
  onSuccess,
}) => {
  const navigate = useNavigate();

  const [targetType, setTargetType] = useState<TargetType>("string");
  const [targetConfig, setTargetConfig] = useState<TargetConfig>("both");
  const [targetContent, setTargetContent] = useState("");
  const [replacementContent, setReplacementContent] = useState("");
  const [insertIndex, setInsertIndex] = useState<string>("");
  const [selectorEnabled, setSelectorEnabled] = useState(false);
  const [selectorName, setSelectorName] = useState("");
  const [selectorType, setSelectorType] = useState("");

  // Helper for dynamic field configuration based on target type
  const getFieldConfig = (type: TargetType) => {
    switch (type) {
      case "add_property":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name to add property to",
          findMultiline: false,
          replaceLabel: "Property JSON",
          replacePlaceholder: '{"visibleIf": "{q1} = \'yes\'}"}',
          replaceMultiline: true,
          replaceRows: 4,
          showReplace: true,
        };
      case "remove_property":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name",
          findMultiline: false,
          replaceLabel: "Property Path",
          replacePlaceholder: "visibleIf or validators[0]",
          replaceMultiline: false,
          replaceRows: 1,
          showReplace: true,
        };
      case "add_element":
        return {
          findLabel: "Container Name",
          findPlaceholder: "Enter page, panel, or paneldynamic name",
          findMultiline: false,
          replaceLabel: "Element JSON",
          replacePlaceholder: '{"type": "text", "name": "new_field", ...}',
          replaceMultiline: true,
          replaceRows: 8,
          showReplace: true,
        };
      case "delete_element":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name to delete",
          findMultiline: false,
          replaceLabel: null,
          replacePlaceholder: null,
          replaceMultiline: false,
          replaceRows: 1,
          showReplace: false,
        };
      case "set_property":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name to set property on",
          findMultiline: false,
          replaceLabel: "Property JSON",
          replacePlaceholder: '{"isRequired": true}',
          replaceMultiline: true,
          replaceRows: 4,
          showReplace: true,
        };
      case "update_property":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name to update property on",
          findMultiline: false,
          replaceLabel: "Property JSON",
          replacePlaceholder: '{"isRequired": true}',
          replaceMultiline: true,
          replaceRows: 4,
          showReplace: true,
        };
      case "rename_property":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name",
          findMultiline: false,
          replaceLabel: "Rename Args",
          replacePlaceholder: '{"from": "enableIf", "to": "visibleIf"}',
          replaceMultiline: true,
          replaceRows: 2,
          showReplace: true,
        };
      case "append_to_array":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name",
          findMultiline: false,
          replaceLabel: "Append Args",
          replacePlaceholder:
            '{"path": "choices", "value": {"value": "X", "text": "X"}, "dedupe": true}',
          replaceMultiline: true,
          replaceRows: 4,
          showReplace: true,
        };
      case "remove_from_array_by_match":
        return {
          findLabel: "Element Name",
          findPlaceholder: "Enter element name",
          findMultiline: false,
          replaceLabel: "Match Args",
          replacePlaceholder: '{"path": "choices", "match": {"value": "X"}}',
          replaceMultiline: true,
          replaceRows: 3,
          showReplace: true,
        };
      case "element":
      case "page":
        return {
          findLabel: "Name",
          findPlaceholder: "Enter exact name",
          findMultiline: false,
          replaceLabel: "Replacement JSON",
          replacePlaceholder: "Enter replacement JSON object",
          replaceMultiline: true,
          replaceRows: 8,
          showReplace: true,
        };
      default:
        return {
          findLabel: "Find",
          findPlaceholder: "Enter text to find (case-sensitive)",
          findMultiline: true,
          replaceLabel: "Replace With",
          replacePlaceholder: "Enter replacement text",
          replaceMultiline: true,
          replaceRows: 3,
          showReplace: true,
        };
    }
  };

  const fieldConfig = getFieldConfig(targetType);

  // Target types where the replacement field must be a JSON value.
  // add_property additionally requires a JSON *object* (not array/primitive).
  const replacementJsonKind: "object" | "any" | null = useMemo(() => {
    switch (targetType) {
      case "add_property":
      case "set_property":
      case "update_property":
      case "rename_property":
      case "append_to_array":
      case "remove_from_array_by_match":
        return "object";
      case "add_element":
      case "element":
      case "page":
        return "any";
      default:
        return null;
    }
  }, [targetType]);

  const replacementJsonError = useMemo<string | null>(() => {
    if (!replacementJsonKind) return null;
    if (!replacementContent.trim()) return null; // empty handled by required flag
    const normalized = normalizeJsonReplacement(replacementContent);
    try {
      const parsed = JSON.parse(normalized);
      if (
        replacementJsonKind === "object" &&
        (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      ) {
        return 'Must be a JSON object like {"key": value}';
      }
      return null;
    } catch (e: any) {
      return `Invalid JSON: ${e.message}`;
    }
  }, [replacementContent, replacementJsonKind]);

  // Single source of truth: undefined when selector is off OR op doesn't support it.
  // UI toggle state (selectorEnabled, selectorName, selectorType) is preserved for
  // convenience so values survive op-type switches — but effectiveSelector gates
  // all actual use (empty-content validation, request body, preview payload).
  const effectiveSelector: Selector | undefined = useMemo(() => {
    if (!selectorEnabled) return undefined;
    if (!SELECTOR_SUPPORTED_OPS.includes(targetType)) return undefined;
    const name = selectorName.trim();
    const type = selectorType.trim();
    if (!name && !type) return undefined;
    return {
      ...(name ? { name } : {}),
      ...(type ? { type } : {}),
    };
  }, [selectorEnabled, targetType, selectorName, selectorType]);

  const [step, setStep] = useState<ViewStep>("search");
  const [isSearching, setIsSearching] = useState(false);

  const [searchResults, setSearchResults] =
    useState<PostBulkEditSearchResponse | null>(null);
  const [selectedSurveys, setSelectedSurveys] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (open) {
      setStep("search");
      setTargetContent("");
      setReplacementContent("");
      setInsertIndex("");
      setSearchResults(null);
      setSelectedSurveys(new Set());
      setSelectorEnabled(false);
      setSelectorName("");
      setSelectorType("");
    }
  }, [open]);

  const handleSearch = async () => {
    const usingSelector = effectiveSelector !== undefined;
    // delete_element doesn't require replacementContent;
    // selector mode allows empty targetContent
    if (
      (!targetContent && !usingSelector) ||
      (!replacementContent && targetType !== "delete_element")
    ) {
      return;
    }

    setIsSearching(true);
    try {
      const response = await postBulkEditSearch({
        path: { org_id: orgId },
        body: {
          target_type: targetType,
          target_content: targetContent,
          target_config: targetConfig,
          ...(effectiveSelector ? { selector: effectiveSelector } : {}),
        },
      });

      setSearchResults(response.data as PostBulkEditSearchResponse);
      const allIds = new Set(
        response.data?.results?.map((r: SurveyMatch) => r.parent_survey_id) ||
          [],
      );
      setSelectedSurveys(allIds);
      setStep("results");
    } catch (error: any) {
      console.error("Search failed:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Search failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePreview = () => {
    if (selectedSurveys.size === 0) {
      return;
    }

    let normalizedReplacement = replacementContent;

    // For delete_element, no replacement needed
    if (targetType === "delete_element") {
      normalizedReplacement = "";
    }
    // For all JSON-replacement ops, normalize once then do any op-specific post-processing
    else if (replacementJsonKind !== null) {
      normalizedReplacement = normalizeJsonReplacement(replacementContent);

      if (targetType === "add_element") {
        // Wrap the element JSON with __insert_index
        try {
          const elementObj = JSON.parse(normalizedReplacement);
          const idx = insertIndex ? parseInt(insertIndex, 10) : 0;
          normalizedReplacement = JSON.stringify({
            __insert_index: isNaN(idx) ? 0 : idx,
            element: elementObj,
          });
        } catch (parseError: any) {
          toast.error(`Invalid element JSON: ${parseError.message}`);
          return;
        }
      } else if (targetType === "add_property") {
        // Validate JSON object shape
        try {
          const parsed = JSON.parse(normalizedReplacement);
          if (typeof parsed !== "object" || Array.isArray(parsed)) {
            toast.error('Property JSON must be an object like {"key": value}');
            return;
          }
        } catch (parseError: any) {
          toast.error(`Invalid property JSON: ${parseError.message}`);
          return;
        }
      } else {
        // All other JSON ops: validate parseable
        try {
          JSON.parse(normalizedReplacement);
        } catch (parseError: any) {
          toast.error(`Invalid replacement JSON: ${parseError.message}`);
          return;
        }
      }
    }
    // For remove_property (replacementJsonKind === null, targetType !== delete_element),
    // no normalization needed \u2014 it's a property path string.

    // Store bulk edit data in sessionStorage to avoid URL encoding issues with JSON
    const bulkEditData = {
      targetType,
      targetConfig,
      targetContent,
      replacementContent: normalizedReplacement,
      surveyIds: Array.from(selectedSurveys),
      selector: effectiveSelector,
    };
    sessionStorage.setItem("bulkEditData", JSON.stringify(bulkEditData));

    navigate(`/rubireportv2/${orgId}/forms/surveys/bulk-edit/preview`);
    onClose();
  };

  const handleToggleSelection = (surveyId: string) => {
    const newSelection = new Set(selectedSurveys);
    if (newSelection.has(surveyId)) {
      newSelection.delete(surveyId);
    } else {
      newSelection.add(surveyId);
    }
    setSelectedSurveys(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(
        searchResults?.results?.map((r: SurveyMatch) => r.parent_survey_id) ||
          [],
      );
      setSelectedSurveys(allIds);
    } else {
      setSelectedSurveys(new Set());
    }
  };

  const surveysWithDrafts =
    searchResults?.results?.filter(
      (r: SurveyMatch) =>
        selectedSurveys.has(r.parent_survey_id) && r.is_draft_source,
    ) || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <EditIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Bulk Edit Surveys
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Step 1: Search Form */}
        {step === "search" && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Find and replace content across multiple surveys in this
              organization. Changes will create draft versions that must be
              deployed individually.
            </Typography>

            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Target Type</InputLabel>
                  <Select
                    value={targetType}
                    onChange={(e) =>
                      setTargetType(e.target.value as TargetType)
                    }
                    label="Target Type"
                  >
                    <MenuItem value="string">String (text anywhere)</MenuItem>
                    <MenuItem value="element">
                      Replace Element (by name)
                    </MenuItem>
                    <MenuItem value="page">Replace Page (by name)</MenuItem>
                    <MenuItem value="add_property">
                      Add Property (to element)
                    </MenuItem>
                    <MenuItem value="remove_property">
                      Remove Property (from element)
                    </MenuItem>
                    <MenuItem value="add_element">
                      Add Element (to container)
                    </MenuItem>
                    <MenuItem value="delete_element">
                      Delete Element (by name)
                    </MenuItem>
                    <MenuItem value="set_property">
                      Set Property (overwrite or create)
                    </MenuItem>
                    <MenuItem value="update_property">
                      Update Property (overwrite only — skip if missing)
                    </MenuItem>
                    <MenuItem value="rename_property">
                      Rename Property Key
                    </MenuItem>
                    <MenuItem value="append_to_array">
                      Append to Array Property
                    </MenuItem>
                    <MenuItem value="remove_from_array_by_match">
                      Remove from Array by Match
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Target Configuration</InputLabel>
                  <Select
                    value={targetConfig}
                    onChange={(e) =>
                      setTargetConfig(e.target.value as TargetConfig)
                    }
                    label="Target Configuration"
                  >
                    <MenuItem value="both">Both (Main + Condensed)</MenuItem>
                    <MenuItem value="main">Main Report Only</MenuItem>
                    <MenuItem value="condensed">Condensed Report Only</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {(targetType === "set_property" ||
                targetType === "update_property") && (
                <Alert severity="info" sx={{ mb: 0 }}>
                  <strong>set</strong> overwrites the property or creates it if
                  missing. <strong>update</strong> only overwrites an existing
                  property — it skips elements that don't already have it.
                </Alert>
              )}

              <TextField
                label={fieldConfig.findLabel}
                value={targetContent}
                onChange={(e) => setTargetContent(e.target.value)}
                multiline={fieldConfig.findMultiline}
                rows={fieldConfig.findMultiline ? 3 : 1}
                placeholder={fieldConfig.findPlaceholder}
                fullWidth
                required
              />

              {fieldConfig.showReplace && (
                <TextField
                  label={fieldConfig.replaceLabel}
                  value={replacementContent}
                  onChange={(e) => setReplacementContent(e.target.value)}
                  multiline={fieldConfig.replaceMultiline}
                  rows={fieldConfig.replaceRows}
                  placeholder={fieldConfig.replacePlaceholder ?? ""}
                  fullWidth
                  required
                  error={!!replacementJsonError}
                  helperText={
                    replacementJsonError ??
                    (replacementJsonKind ? "Must be valid JSON" : undefined)
                  }
                  inputProps={
                    replacementJsonKind
                      ? {
                          spellCheck: false,
                          autoCorrect: "off",
                          autoCapitalize: "off",
                          style: { fontFamily: "monospace" },
                        }
                      : undefined
                  }
                />
              )}

              {targetType === "add_element" && (
                <TextField
                  label="Insert Index (optional)"
                  value={insertIndex}
                  onChange={(e) => setInsertIndex(e.target.value)}
                  type="number"
                  placeholder="0 = top (default)"
                  helperText="Position to insert the element (0 = first position)"
                  fullWidth
                />
              )}

              {SELECTOR_SUPPORTED_OPS.includes(targetType) && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectorEnabled}
                        onChange={(e) => setSelectorEnabled(e.target.checked)}
                      />
                    }
                    label="Use advanced selector (match many elements)"
                  />
                  {selectorEnabled && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, the Element Name above is ignored. The
                        selector matches by name pattern and/or element type.
                      </Typography>
                      <TextField
                        size="small"
                        label="Name glob (e.g. person-*_firstName)"
                        value={selectorName}
                        onChange={(e) => setSelectorName(e.target.value)}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Element type (e.g. dropdown)"
                        value={selectorType}
                        onChange={(e) => setSelectorType(e.target.value)}
                        fullWidth
                      />
                    </>
                  )}
                </Box>
              )}

              <Alert severity="info">
                All bulk edits create draft versions only. You must deploy each
                survey individually to make changes active.
              </Alert>
            </Box>
          </Box>
        )}

        {/* Step 2: Results Table */}
        {step === "results" && searchResults && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Search Results ({searchResults.total_surveys} surveys,{" "}
                {searchResults.total_matches} matches)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSurveys.size} of {searchResults.results?.length || 0}{" "}
                selected
              </Typography>
            </Box>

            {surveysWithDrafts.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>
                  {surveysWithDrafts.length} selected survey(s) have existing
                  drafts.
                </strong>
                <br />
                These drafts will be updated with the bulk edit changes. You can
                stack multiple bulk edits before deploying.
              </Alert>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedSurveys.size > 0 &&
                          selectedSurveys.size <
                            (searchResults.results?.length || 0)
                        }
                        checked={
                          searchResults.results &&
                          searchResults.results.length > 0 &&
                          selectedSurveys.size === searchResults.results.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Matches</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.results?.map((result: SurveyMatch) => (
                    <TableRow
                      key={result.parent_survey_id}
                      hover
                      onClick={() =>
                        handleToggleSelection(result.parent_survey_id)
                      }
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSurveys.has(result.parent_survey_id)}
                        />
                      </TableCell>
                      <TableCell>{result.survey_name}</TableCell>
                      <TableCell>{result.source_version_number}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${result.match_count} matches`}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {result.is_draft_source ? (
                          <Chip
                            label="Updating Draft"
                            color="info"
                            size="small"
                          />
                        ) : (
                          <Chip label="Creating Draft" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === "search" && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={
                isSearching ||
                (!targetContent && effectiveSelector === undefined) ||
                (fieldConfig.showReplace && !replacementContent) ||
                !!replacementJsonError
              }
              startIcon={
                isSearching ? <CircularProgress size={20} /> : <SearchIcon />
              }
            >
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </>
        )}

        {step === "results" && (
          <>
            <Button onClick={() => setStep("search")}>Back</Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreview}
              disabled={selectedSurveys.size === 0}
            >
              Preview Changes
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
